import type { Meta, StoryObj } from '@storybook/angular';
import { expect } from 'storybook/test';
import { TruckIconComponent } from '../../../app/core/svg-icons/truck-icon.component';

const meta: Meta<TruckIconComponent> = {
  title: 'Atoms/Icons/TruckIcon',
  component: TruckIconComponent,
  parameters: {
    docs: {
      description: {
        component: 'A delivery truck icon component. Perfect for shipping information, delivery status, and logistics-related features.',
      },
    },
  },
};

export default meta;
type Story = StoryObj<TruckIconComponent>;

export const Default: Story = {
  render: () => ({
    template: '<app-truck-icon></app-truck-icon>',
  }),
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-truck-icon')).toBeTruthy();
  },
};

export const DeliveryInfo: Story = {
  render: () => ({
    template: `
      <div style="border: 1px solid #ddd; border-radius: 8px; padding: 1.5rem; background-color: #f8f9fa; max-width: 400px;">
        <div style="display: flex; align-items: center; gap: 1rem; margin-bottom: 1rem;">
          <app-truck-icon></app-truck-icon>
          <h4 style="margin: 0; color: #5C0A31;">Gratis levering</h4>
        </div>
        <p style="margin: 0; color: #666;">
          Vi leverer gratis til hele Danmark ved køb over 500 kr.
        </p>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Truck icon used in delivery information section.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-truck-icon')).toBeTruthy();
  },
};

export const ShippingOptions: Story = {
  render: () => ({
    template: `
      <div style="display: flex; flex-direction: column; gap: 1rem; max-width: 500px;">
        <h4>Leveringsmuligheder</h4>
        
        <div style="display: flex; align-items: center; gap: 1rem; padding: 1rem; border: 1px solid #ddd; border-radius: 8px;">
          <app-truck-icon></app-truck-icon>
          <div style="flex: 1;">
            <strong>Standard levering</strong><br>
            <span style="color: #666;">1-3 arbejdsdage</span>
          </div>
          <span style="color: #5C0A31; font-weight: bold;">39 kr</span>
        </div>
        
        <div style="display: flex; align-items: center; gap: 1rem; padding: 1rem; border: 1px solid #ddd; border-radius: 8px;">
          <app-truck-icon></app-truck-icon>
          <div style="flex: 1;">
            <strong>Express levering</strong><br>
            <span style="color: #666;">Næste arbejdsdag</span>
          </div>
          <span style="color: #5C0A31; font-weight: bold;">89 kr</span>
        </div>
        
        <div style="display: flex; align-items: center; gap: 1rem; padding: 1rem; border: 2px solid #28A745; border-radius: 8px; background-color: #D4EDDA;">
          <app-truck-icon></app-truck-icon>
          <div style="flex: 1;">
            <strong>Gratis levering</strong><br>
            <span style="color: #666;">Ved køb over 500 kr</span>
          </div>
          <span style="color: #28A745; font-weight: bold;">Gratis</span>
        </div>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Truck icons used in shipping options selection.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-truck-icon')).toBeTruthy();
  },
};

export const OrderTracking: Story = {
  render: () => ({
    template: `
      <div style="max-width: 600px;">
        <h4>Spor din ordre</h4>
        <div style="border: 1px solid #ddd; border-radius: 8px; padding: 1.5rem;">
          <div style="display: flex; flex-direction: column; gap: 1.5rem;">
            
            <div style="display: flex; align-items: center; gap: 1rem;">
              <div style="width: 40px; height: 40px; background-color: #28A745; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white;">
                ✓
              </div>
              <div style="flex: 1;">
                <strong>Ordre bekræftet</strong><br>
                <span style="color: #666;">I dag kl. 10:30</span>
              </div>
            </div>
            
            <div style="display: flex; align-items: center; gap: 1rem;">
              <div style="width: 40px; height: 40px; background-color: #28A745; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white;">
                ✓
              </div>
              <div style="flex: 1;">
                <strong>Pakket og klar til afsendelse</strong><br>
                <span style="color: #666;">I dag kl. 14:15</span>
              </div>
            </div>
            
            <div style="display: flex; align-items: center; gap: 1rem;">
              <div style="width: 40px; height: 40px; background-color: #F39C12; border-radius: 50%; display: flex; align-items: center; justify-content: center;">
                <app-truck-icon></app-truck-icon>
              </div>
              <div style="flex: 1;">
                <strong>Afsendt</strong><br>
                <span style="color: #666;">Forventet levering i morgen</span>
              </div>
            </div>
            
            <div style="display: flex; align-items: center; gap: 1rem; opacity: 0.5;">
              <div style="width: 40px; height: 40px; background-color: #DDD; border-radius: 50%; display: flex; align-items: center; justify-content: center;">
                ✓
              </div>
              <div style="flex: 1;">
                <strong>Leveret</strong><br>
                <span style="color: #666;">Endnu ikke leveret</span>
              </div>
            </div>
            
          </div>
        </div>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Truck icon used in order tracking to show shipping status.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-truck-icon')).toBeTruthy();
  },
};

export const FeatureHighlight: Story = {
  render: () => ({
    template: `
      <div style="background-color: #5C0A31; color: white; padding: 2rem; border-radius: 8px; text-align: center;">
        <div style="display: flex; justify-content: center; margin-bottom: 1rem;">
          <app-truck-icon></app-truck-icon>
        </div>
        <h3 style="margin: 0 0 1rem 0;">Hurtig levering</h3>
        <p style="margin: 0; opacity: 0.9;">
          Vi leverer dine vine hurtigt og sikkert til hele Skandinavien
        </p>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Truck icon used as a feature highlight on dark wine background.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-truck-icon')).toBeTruthy();
  },
};

export const InProductCard: Story = {
  render: () => ({
    template: `
      <div style="border: 1px solid #ddd; border-radius: 8px; padding: 1rem; max-width: 300px;">
        <div style="background-color: #f8f9fa; height: 150px; margin-bottom: 1rem; border-radius: 4px; display: flex; align-items: center; justify-content: center; color: #666;">
          [Vin billede]
        </div>
        <h5>Champagne Bollinger</h5>
        <p style="color: #666; margin: 0.5rem 0;">Champagne, Frankrig</p>
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem;">
          <strong style="color: #5C0A31;">899 kr</strong>
        </div>
        
        <div style="display: flex; align-items: center; gap: 0.5rem; padding: 0.5rem; background-color: #D4EDDA; border-radius: 4px; font-size: 0.875rem;">
          <app-truck-icon></app-truck-icon>
          <span style="color: #155724;">Gratis levering</span>
        </div>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Truck icon used in product card to highlight free shipping.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-truck-icon')).toBeTruthy();
  },
};
